/**
 * A Vintage Fit custom elements.
 */

/**
 * Category card (sections/category-grid.liquid): cycles through its photos while hovered. On devices without hover,
 * it cycles automatically while the card is visible.
 */
if (!customElements.get('category-card')) {
  customElements.define('category-card', class extends HTMLElement {
    connectedCallback() {
      this.images = Array.from(this.querySelectorAll('.category-card__image'));

      if (this.images.length < 2) {
        return;
      }

      this.currentIndex = 0;

      if (window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('pointerenter', () => this.start());
        this.addEventListener('pointerleave', () => this.stop(true));
      } else {
        this.observer = new IntersectionObserver((entries) => {
          entries[0].isIntersecting ? this.start() : this.stop(false);
        }, { threshold: 0.5 });

        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.stop(false);
      this.observer?.disconnect();
    }

    start() {
      if (this.timer) {
        return;
      }

      this.images.forEach((image) => image.loading = 'eager');
      this.show(this.currentIndex + 1);
      this.timer = setInterval(() => this.show(this.currentIndex + 1), parseInt(this.getAttribute('interval'), 10) || 1000);
    }

    stop(reset) {
      clearInterval(this.timer);
      this.timer = null;

      if (reset) {
        this.show(0);
      }
    }

    show(index) {
      this.images[this.currentIndex]?.classList.remove('is-active');
      this.currentIndex = index % this.images.length;
      this.images[this.currentIndex].classList.add('is-active');
    }
  });
}

/**
 * Exposes the countdown bar height as a CSS variable, so the full screen hero can account for it.
 */
(() => {
  const update = () => {
    const bar = document.querySelector('.countdown-bar');
    document.documentElement.style.setProperty('--countdown-bar-height', `${bar ? bar.offsetHeight : 0}px`);
  };

  update();
  document.addEventListener('DOMContentLoaded', update);
  window.addEventListener('load', update);
  window.addEventListener('resize', update);

  const bar = document.querySelector('.countdown-bar');

  if (bar && 'ResizeObserver' in window) {
    new ResizeObserver(update).observe(bar);
  }
})();

/**
 * Mega menu: hovering a collection link swaps the promo images with photos of that collection. The original images
 * come back when the pointer leaves the menu.
 */
(() => {
  const preloaded = new Map();

  const preload = (src) => {
    if (!preloaded.has(src)) {
      const image = new Image();
      image.src = src;
      preloaded.set(src, image.decode().catch(() => {}));
    }

    return preloaded.get(src);
  };

  const preloadMenu = (menu) => {
    if (menu.avfPreloaded) {
      return;
    }

    menu.avfPreloaded = true;
    menu.querySelectorAll('[data-preview]').forEach((link) => {
      try {
        JSON.parse(link.dataset.preview).forEach((data) => preload(data.src));
      } catch (error) {}
    });
  };

  const swap = (card, data) => {
    const image = card.querySelector('.mega-menu-card__media img');
    const title = card.querySelector('.mega-menu-card__title');

    // Image, title and link are updated together, once the new photo is ready
    card.avfPending = data.src;

    preload(data.src).then(() => {
      if (card.avfPending !== data.src) {
        return;
      }

      if (image) {
        image.removeAttribute('srcset');
        image.src = data.src;
      }

      if (title) {
        title.textContent = data.title;
      }

      if (card.tagName === 'A') {
        card.href = data.url;
      }
    });
  };

  const snapshot = (card) => {
    const image = card.querySelector('.mega-menu-card__media img');

    return {
      src: image?.getAttribute('src'),
      srcset: image?.getAttribute('srcset'),
      title: card.querySelector('.mega-menu-card__title')?.textContent,
      url: card.getAttribute('href')
    };
  };

  const restore = (menu) => {
    menu.querySelectorAll('.mega-menu-card').forEach((card) => {
      if (!card.avfOriginal) {
        return;
      }

      card.avfPending = null;

      const { src, srcset, title, url } = card.avfOriginal;
      const image = card.querySelector('.mega-menu-card__media img');

      if (image && src) {
        image.src = src;
        srcset && image.setAttribute('srcset', srcset);
      }

      const titleElement = card.querySelector('.mega-menu-card__title');

      if (titleElement && title) {
        titleElement.textContent = title;
      }

      if (url) {
        card.href = url;
      }
    });
  };

  document.addEventListener('pointerover', (event) => {
    const openMenu = event.target.closest?.('.mega-menu, .header__primary-nav-item, .header__primary-nav li');
    openMenu?.querySelectorAll('.mega-menu').forEach(preloadMenu);
    openMenu?.classList.contains('mega-menu') && preloadMenu(openMenu);

    const link = event.target.closest?.('.mega-menu [data-preview]');

    if (!link) {
      return;
    }

    const menu = link.closest('.mega-menu');
    const cards = Array.from(menu.querySelectorAll('.mega-menu-card'));
    let previews = [];

    try {
      previews = JSON.parse(link.dataset.preview);
    } catch (error) {
      return;
    }

    menu.querySelectorAll('.mega-menu__link.is-active').forEach((active) => active.classList.remove('is-active'));
    link.classList.add('is-active');

    cards.forEach((card, index) => {
      card.avfOriginal ??= snapshot(card);

      if (previews[index]) {
        swap(card, previews[index]);
      }
    });
  });

  document.addEventListener('pointerout', (event) => {
    const menu = event.target.closest?.('.mega-menu');

    if (menu && !menu.contains(event.relatedTarget)) {
      menu.querySelectorAll('.mega-menu__link.is-active').forEach((active) => active.classList.remove('is-active'));
      restore(menu);
    }
  });
})();

/**
 * Instagram magazine spread (instagram-feed section, magazine layout): thumbnails swap the large photo with a
 * crossfade. Optionally advances on its own, pausing while hovered or focused.
 */
if (!customElements.get('ig-magazine')) {
  customElements.define('ig-magazine', class extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.ig-magazine__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.ig-magazine__thumb'));
      this.posts = Array.from(this.querySelectorAll('.ig-post'));
      this.index = 0;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.show(parseInt(thumb.dataset.index, 10));
          this.restart();
        });
      });

      this.addEventListener('pointerenter', () => this.paused = true);
      this.addEventListener('pointerleave', () => this.paused = false);
      this.addEventListener('focusin', () => this.paused = true);
      this.addEventListener('focusout', () => this.paused = false);

      this.restart();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    restart() {
      clearInterval(this.timer);
      const delay = parseInt(this.getAttribute('autoplay'), 10);

      if (delay && this.slides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.timer = setInterval(() => !this.paused && this.show(this.index + 1), delay);
      }
    }

    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('aria-hidden', !active);
        slide.toggleAttribute('inert', !active);

        if (active) {
          slide.querySelector('img')?.setAttribute('loading', 'eager');
        }
      });

      const counter = this.querySelector('.ig-magazine__counter-current');

      if (counter) {
        counter.textContent = String(this.index + 1).padStart(2, '0');
      }

      this.posts.forEach((post, i) => {
        post.classList.toggle('is-active', i === this.index);
        post.hidden = i !== this.index;
      });

      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.index);
        thumb.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
      });

      // Only the thumbnail strip scrolls, never the page
      const strip = this.querySelector('.ig-magazine__thumbs');
      const thumb = this.thumbs[this.index];

      if (strip && thumb && strip.scrollWidth > strip.clientWidth) {
        strip.scrollTo({ left: thumb.offsetLeft - strip.offsetLeft, behavior: 'smooth' });
      }
    }
  });
}

/**
 * Instagram carousel (instagram-feed section, carousel layout): arrows scroll the row, clicking a post opens a
 * pop-up with the post and its product, where customers can shop or move to the previous / next post.
 */
if (!customElements.get('ig-carousel')) {
  customElements.define('ig-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ig-carousel__track');
      this.dialog = this.querySelector('.ig-lightbox');
      this.items = Array.from(this.querySelectorAll('.ig-carousel__item'));
      this.cards = Array.from(this.querySelectorAll('.ig-lightbox__slide'));
      this.products = Array.from(this.querySelectorAll('.ig-lightbox__product'));
      this.index = 0;

      this.querySelectorAll('.ig-carousel__nav .ig-carousel__arrow').forEach((arrow) => {
        arrow.addEventListener('click', () => {
          const step = this.items[0]?.getBoundingClientRect().width || 300;
          this.track.scrollBy({ left: step * parseInt(arrow.dataset.direction, 10), behavior: 'smooth' });
        });
      });

      this.items.forEach((item) => {
        item.addEventListener('click', () => this.open(parseInt(item.dataset.index, 10)));
      });

      this.querySelectorAll('.ig-lightbox [data-step]').forEach((arrow) => {
        arrow.addEventListener('click', () => this.show(this.index + parseInt(arrow.dataset.step, 10)));
      });

      this.querySelector('.ig-lightbox__close')?.addEventListener('click', () => this.dialog.close());

      // Click on the backdrop closes the pop-up
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          this.dialog.close();
        }
      });

      this.dialog?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.show(this.index + 1);
        if (event.key === 'ArrowLeft') this.show(this.index - 1);
      });

      this.dialog?.addEventListener('close', () => document.documentElement.classList.remove('ig-lightbox-open'));
    }

    open(index) {
      this.show(index);
      this.dialog.showModal();
      document.documentElement.classList.add('ig-lightbox-open');
    }

    show(index) {
      const count = this.cards.length;
      this.index = (index + count) % count;

      this.cards.forEach((card, i) => {
        const active = i === this.index;
        card.classList.toggle('is-active', active);
        card.toggleAttribute('aria-hidden', !active);
        card.toggleAttribute('inert', !active);

        if (active) {
          card.querySelector('img')?.setAttribute('loading', 'eager');
        }
      });

      this.products.forEach((product, i) => {
        product.classList.toggle('is-active', i === this.index);
        product.hidden = i !== this.index;

        if (i === this.index) {
          product.querySelector('img')?.setAttribute('loading', 'eager');
        }
      });

      const current = this.querySelector('.ig-lightbox__current');

      if (current) {
        current.textContent = this.index + 1;
      }
    }
  });
}
